/*
 * 1DS JS SDK Web Analytics plugin, 3.1.9
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* AutoCaptureHandler.ts
* @author Ram Thiru (ramthi) and Hector Hernandez (hectorh)
* @copyright Microsoft 2018
*/
import { _debounce, _getScrollOffset, _isRightClick, _isLeftClick, _isKeyboardEnter, _isKeyboardSpace, _isMiddleClick, } from "../common/Utils";
import { ActionType } from "../Enums";
import { onDomLoaded } from "../DataCollector";
import { addPageUnloadEventListener, getWindow, getDocument, addPageHideEventListener } from "@microsoft/1ds-core-js";
var clickCaptureInputTypes = { BUTTON: true, CHECKBOX: true, RADIO: true, RESET: true, SUBMIT: true };
var AutoCaptureHandler = /** @class */ (function () {
    /**
    * @param analyticsPlugin - WebAnalytics plugin
    * @param traceLogger - Trace logger to log to console.
    */
    function AutoCaptureHandler(_analyticsPlugin, _traceLogger) {
        this._analyticsPlugin = _analyticsPlugin;
        this._traceLogger = _traceLogger;
    }
    AutoCaptureHandler.prototype.pageView = function () {
        this._analyticsPlugin.capturePageView({ isAuto: true });
    };
    AutoCaptureHandler.prototype.onLoad = function () {
        var _this = this;
        onDomLoaded(function () {
            _this._analyticsPlugin.capturePageViewPerformance({ isAuto: true });
            _this._analyticsPlugin.captureContentUpdate({ isAuto: true, isDomComplete: true });
        });
    };
    // handle automatic event firing on user click
    AutoCaptureHandler.prototype.click = function () {
        var _this = this;
        var win = getWindow();
        var doc = getDocument();
        if (win && win.addEventListener) {
            // IE9 onwards addEventListener is available, 'click' event captures mouse click. mousedown works on other browsers
            var event = (navigator.appVersion.indexOf("MSIE") !== -1) ? "click" : "mousedown";
            win.addEventListener(event, function (evt) { _this._processClick(evt); }, false);
            win.addEventListener("keyup", function (evt) { _this._processClick(evt); }, false);
        }
        else if (doc && doc.attachEvent) {
            // IE8 and below doesn't have addEventListener so it will use attachEvent
            // attaching to window does not work in IE8
            doc.attachEvent("onclick", function (evt) { _this._processClick(evt); });
            doc.attachEvent("keyup", function (evt) { _this._processClick(evt); });
        }
    };
    // handle automatic event firing on user scroll
    AutoCaptureHandler.prototype.scroll = function (debounceConfig) {
        var _this = this;
        var processScroll = _debounce(
        // on first call do nothing
        null, 
        // after debounce, send contentView with viewportOffset
        function () {
            _this._analyticsPlugin.captureContentUpdate({ isAuto: true, actionType: ActionType.SCROLL });
        }, debounceConfig.scroll, this);
        var win = getWindow();
        if (win) {
            if (win.addEventListener) {
                win.addEventListener("scroll", processScroll);
            }
            else if (window.attachEvent) {
                window.attachEvent("onscroll", processScroll);
            }
        }
    };
    // handle automatic event firing on user scroll
    AutoCaptureHandler.prototype.maxScroll = function (maxScroll) {
        var getMaxScrollDepth = function () {
            var currentScroll = _getScrollOffset();
            maxScroll.v = maxScroll.v > currentScroll.v ? maxScroll.v : currentScroll.v;
        };
        var win = getWindow();
        if (win) {
            if (win.addEventListener) {
                win.addEventListener("scroll", getMaxScrollDepth);
            }
            else if (win.attachEvent) {
                win.attachEvent("onscroll", getMaxScrollDepth);
            }
        }
    };
    // handle automatic event firing on user resize
    AutoCaptureHandler.prototype.resize = function (debounceConfig) {
        /// I wasn�t able to repro the bug
        /// (https://microsoft.visualstudio.com/DefaultCollection/OSGS/_workitems/edit/7958464)
        /// until new content was loaded on the page and the resize
        /// rendering became slow. When resize happens, the page rendering
        /// time is longer than the resize debounce, and resize render
        /// happens in time > debounce, and so resize is acting like
        /// it's being done twice: once at the beginning of user resize,
        /// one at the end of browser render resize.
        var _this = this;
        var processResize = _debounce(
        // on first call, send resize contentUpdate
        function () {
            _this._analyticsPlugin.captureContentUpdate({ isAuto: true, actionType: ActionType.RESIZE });
        }, 
        // after debounce, do nothing
        null, debounceConfig.resize, this);
        var win = getWindow();
        if (win) {
            if (win.addEventListener) {
                win.addEventListener("resize", processResize);
            }
            else if (win.attachEvent) {
                win.attachEvent("onresize", processResize);
            }
        }
    };
    AutoCaptureHandler.prototype.onUnload = function () {
        var thePlugin = this._analyticsPlugin;
        function _doUnload() {
            if (thePlugin) {
                thePlugin.capturePageUnload({ isAuto: true });
            }
        }
        if (thePlugin) {
            var coreConfig = ((thePlugin || {}).core || {}).config;
            var excludePageUnloadEvents = coreConfig.disablePageUnloadEvents;
            addPageUnloadEventListener(_doUnload, excludePageUnloadEvents);
            addPageHideEventListener(_doUnload, excludePageUnloadEvents);
        }
    };
    AutoCaptureHandler.prototype._processClick = function (clickEvent) {
        var clickCaptureElements = { A: true, BUTTON: true, AREA: true, INPUT: true };
        var win = getWindow();
        clickEvent = clickEvent || win.event; // IE 8 does not pass the event
        var element = clickEvent.srcElement || clickEvent.target;
        // populate overrideValues
        var overrideValues = {
            isAuto: true,
            clickCoordinateX: clickEvent.pageX,
            clickCoordinateY: clickEvent.pageY
        };
        var isRightClick = _isRightClick(clickEvent);
        if (isRightClick) {
            overrideValues.actionType = ActionType.CLICKRIGHT;
        }
        else if (_isLeftClick(clickEvent)) {
            overrideValues.actionType = ActionType.CLICKLEFT;
        }
        else if (_isKeyboardEnter(clickEvent)) {
            overrideValues.actionType = ActionType.KEYBOARDENTER;
        }
        else if (_isKeyboardSpace(clickEvent)) {
            overrideValues.actionType = ActionType.KEYBOARDSPACE;
        }
        else if (_isMiddleClick(clickEvent)) {
            overrideValues.actionType = ActionType.CLICKMIDDLE;
        }
        else {
            return;
        }
        while (element && element.tagName) {
            // control property will be available for <label> elements with 'for' attribute, only use it when is a
            // valid JSLL capture element to avoid infinite loops
            if (element.control && clickCaptureElements[element.control.tagName.toUpperCase()]) {
                element = element.control;
            }
            if (!clickCaptureElements[element.tagName.toUpperCase()]) {
                element = element.parentElement || element.parentNode;
                continue;
            }
            else {
                // Check allowed INPUT types
                var sendEvent = element.tagName.toUpperCase() === "INPUT" ? clickCaptureInputTypes[element.type.toUpperCase()] : true;
                if (sendEvent) {
                    this._analyticsPlugin.capturePageAction(element, overrideValues, {}, isRightClick);
                }
                break;
            }
        }
    };
    return AutoCaptureHandler;
}());
export { AutoCaptureHandler };
//# sourceMappingURL=AutoCaptureHandler.js.map